import { Loader2, RotateCcw, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface JobProgressProps {
	/** Short verb phrase, e.g. "Listing on eBay". */
	label: string;
	done: number;
	total: number;
	failed?: number;
	/** Hide the bar/counters and show a done state once finished. */
	isRunning?: boolean;
	onRetryFailed?: () => void;
	onCancel?: () => void;
	className?: string;
}

/**
 * Tier 2 of the progress vocabulary: a multi-step job the user is waiting on.
 *
 * Determinate bar + "X of Y" counter + failure count, rendered inline in the
 * surface that launched the job. Works identically on mobile and desktop —
 * no toasts involved.
 */
export function JobProgress({
	label,
	done,
	total,
	failed = 0,
	isRunning = true,
	onRetryFailed,
	onCancel,
	className,
}: JobProgressProps) {
	const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
	const complete = !isRunning && total > 0 && done >= total;

	if (total === 0) return null;

	return (
		<div
			className={cn(
				'rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5',
				className,
			)}
			role="status"
			aria-live="polite"
		>
			<div className="flex flex-wrap items-center gap-2 text-xs tabular-nums">
				{isRunning ? (
					<span className="inline-flex items-center gap-1.5 text-active-gold">
						<Loader2 className="w-3.5 h-3.5 animate-spin" />
						{label} {done} of {total}
					</span>
				) : (
					<span
						className={cn(
							'inline-flex items-center gap-1.5',
							complete && failed === 0 ? 'text-success' : 'text-muted-foreground',
						)}
					>
						{complete && failed === 0 ? (
							<CheckCircle2 className="w-3.5 h-3.5" />
						) : null}
						{label} — {done} of {total} done
					</span>
				)}

				{failed > 0 && (
					<span className="inline-flex items-center gap-1.5 text-destructive">
						<AlertCircle className="w-3.5 h-3.5" />
						{failed} failed
					</span>
				)}

				<div className="ml-auto flex items-center gap-1">
					{failed > 0 && onRetryFailed && (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							onClick={onRetryFailed}
							className="h-7 gap-1 rounded-full text-xs px-2.5 text-active-gold hover:text-active-gold hover:bg-active-gold/10"
						>
							<RotateCcw className="w-3.5 h-3.5" />
							Retry failed
						</Button>
					)}
					{isRunning && onCancel && (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							onClick={onCancel}
							className="h-7 rounded-full text-xs px-2.5 text-muted-foreground"
						>
							Cancel
						</Button>
					)}
				</div>
			</div>

			<div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-white/[0.06]">
				<div
					className="h-full rounded-full bg-active-gold transition-[width] duration-300 ease-out"
					style={{ width: `${pct}%` }}
				/>
			</div>
		</div>
	);
}
